import {Graphics, Point, SimpleMesh, Text, Texture} from 'pixi.js';
import {CardData, CardType} from '../../../../../../data/active_data/field_config/card_stack_data/CardData';
import {TextFactory} from '../../../../../../factories/TextFactory';
import {Pivot} from '../../../../../../utils/Pivot';

export class CardView extends SimpleMesh
{
    private topCenterPoint: Point;
    private bottomCenterPoint: Point;
    private readonly cardWidthHalf: number;
    private readonly backTexture: Texture;
    private alreadyFlipped: boolean;
    private wid: number;
    private hei: number;
    private textField: Text | undefined;
    private randomCardBorder: Graphics | undefined;

    constructor(cardData: CardData, frontTexture: Texture, backTexture: Texture, stackId?: number) {
        let width: number = cardData.width;
        let height: number = cardData.height;
        let cardWidthHalf: number = width / 2;
        let vertices: number[] = [
            0, 0,
            cardWidthHalf, 0,
            width, 0,
            0, height,
            cardWidthHalf, height,
            width, height
        ];

        let uvs: number[] = [
            0, 0,
            .5, 0,
            1, 0,
            0, 1,
            .5, 1,
            1, 1
        ];

        let indices: number[] = [
            0, 1, 3,
            3, 1, 4,
            4, 1, 2,
            2, 4, 5
        ];

        if (cardData.type == CardType.RANDOM) {
            frontTexture = backTexture = Texture.WHITE;
        }

        super(frontTexture, new Float32Array(vertices), new Float32Array(uvs), new Float32Array(indices));
        this.alreadyFlipped = false;
        this._hoverScale = 1;
        this.backTexture = backTexture;
        this.cardWidthHalf = cardWidthHalf;
        this.topCenterPoint = new Point(this.cardWidthHalf, 0);
        this.bottomCenterPoint = new Point(this.cardWidthHalf, height);
        this.wid = width;
        this.hei = height;
        this._flipCorner = 0;

        this.tryAddRandomCardProperties(cardData, stackId!);
    }

    private _hoverScale: number;

    get hoverScale(): number {
        return this._hoverScale;
    }

    set hoverScale(value: number) {
        this._hoverScale = value;
    }

    private _flipCorner: number;

    get flipCorner(): number {
        return this._flipCorner;
    }

    set flipCorner(corner: number) {
        if (corner > Math.PI / 2 && !this.alreadyFlipped) {
            this.alreadyFlipped = true;
            this.texture = this.backTexture;
        }
        this._flipCorner = corner;
        let halfCos: number = this.cardWidthHalf * Math.cos(corner);
        let smallSin: number = -30 * Math.sin(corner);

        if (this.alreadyFlipped) {
            halfCos = -halfCos;
            smallSin = -smallSin;
        }
        let pointMinusHalfCos: number = this.topCenterPoint.x - halfCos;
        let pointPlusHalfCos: number = this.topCenterPoint.x + halfCos;
        let vertices: number[] = [
            pointMinusHalfCos, -smallSin / 2,
            this.cardWidthHalf, 0,
            pointPlusHalfCos, smallSin,
            pointMinusHalfCos, this.hei + smallSin / 2,
            this.cardWidthHalf, this.hei,
            pointPlusHalfCos, this.hei - smallSin
        ];
        this.verticesBuffer.update(vertices);
    }

    scaleToHeight(height: number): number {
        return this.scale.x = this.scale.y = height / this.hei * this._hoverScale;
    }

    private getRandomCardText(cardData: CardData, stackId: number): string {
        return `Random el.\n${cardData.id}\nfrom stack\n${stackId > -1 ? stackId : '[new]'}\n\n` + (cardData.flipped ? 'Back' : 'Front');
    }

    private tryAddRandomCardProperties(cardData: CardData, stackId: number) {
        if (cardData.type == CardType.RANDOM) {
            this.textField = TextFactory.create(this.getRandomCardText(cardData, stackId!), 32, 0x0);
            this.addChild(this.textField);
            this.textField.x = cardData.width / 2;
            this.textField.y = cardData.height / 2;
            this.interactive = this.interactiveChildren = false;
            Pivot.centerPivot(this.textField);
            this.randomCardBorder = new Graphics();
            this.randomCardBorder.lineStyle(3);
            let margin: number = 10;
            let marginX2: number = margin * 2;
            this.randomCardBorder.drawRoundedRect(margin, margin, cardData.width - marginX2, cardData.height - marginX2, 20);
            this.addChild(this.randomCardBorder);
        }
    }
}
